import React, { useState, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import {
  DISPLAY_UNITS,
  convertEnergy,
  fuelQuantityToMJ,
  formatEnergyValue,
  FUEL_NCV,
} from '../../utils/energyUnits';

/**
 * Inline popover that shows a quantity converted across common energy units.
 *
 * Modes:
 *  - mode="energy": value is already in an energy unit (e.g. kWh on the
 *    electricity tab). Pass `unit` = the energy unit of `value`.
 *  - mode="fuel": value is a fuel quantity (L, kg, SCM). Pass `fuelType`
 *    (e.g. "Diesel") + `unit` (e.g. "L"). Conversion uses NCV from
 *    utils/energyUnits.js — must match the fuel's native unit.
 *
 * Renders an unobtrusive Info icon button; clicking opens the panel.
 */
const EnergyUnitPopover = ({ value, unit, mode = 'energy', fuelType = null }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const numericValue = Number(value);
  const valid = Number.isFinite(numericValue) && numericValue !== 0;

  // Compute the conversion table.
  let rows = [];
  let errorMsg = null;

  if (valid) {
    if (mode === 'fuel') {
      const master = FUEL_NCV[fuelType];
      if (!master) {
        errorMsg = `No calorific value on file for "${fuelType || 'this fuel'}".`;
      } else if (master.unit !== unit) {
        errorMsg = `Cannot convert ${unit} → energy without a density. Master defines ${fuelType} in ${master.unit}.`;
      } else {
        const mj = fuelQuantityToMJ(numericValue, fuelType, unit);
        rows = DISPLAY_UNITS.map((u) => ({ unit: u, val: convertEnergy(mj, 'MJ', u) }));
      }
    } else {
      if (!DISPLAY_UNITS.includes(unit) && unit !== 'MJ' && unit !== 'kWh') {
        errorMsg = `Unknown energy unit "${unit}".`;
      } else {
        rows = DISPLAY_UNITS.map((u) => ({ unit: u, val: convertEnergy(numericValue, unit, u) }));
      }
    }
  }

  return (
    <span ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="ml-1 text-gray-400 hover:text-blue-600 align-middle"
        title="Show this value in other energy units"
        aria-label="Convert energy units"
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div
          className="absolute z-50 right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
            <span className="font-semibold text-gray-700">Energy unit conversion</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {!valid && (
            <p className="text-gray-500 italic">Enter a non-zero quantity to see conversions.</p>
          )}

          {valid && errorMsg && (
            <p className="text-amber-600">{errorMsg}</p>
          )}

          {valid && !errorMsg && (
            <>
              <p className="text-gray-500 mb-2">
                {mode === 'fuel' ? (
                  <>
                    {numericValue.toLocaleString()} {unit} of <b>{fuelType}</b> equals:
                  </>
                ) : (
                  <>{numericValue.toLocaleString()} {unit} equals:</>
                )}
              </p>
              <ul className="space-y-0.5">
                {rows.map(({ unit: u, val }) => (
                  <li key={u} className="flex justify-between gap-3">
                    <span className="text-gray-600">{u}</span>
                    <span className="font-mono text-gray-900">{formatEnergyValue(val)}</span>
                  </li>
                ))}
              </ul>
              {mode === 'fuel' && (
                <p className="mt-2 pt-2 border-t border-gray-100 text-gray-400">
                  NCV: {FUEL_NCV[fuelType]?.ncv} MJ/{FUEL_NCV[fuelType]?.unit} (IPCC AR6 defaults)
                </p>
              )}
            </>
          )}
        </div>
      )}
    </span>
  );
};

export default EnergyUnitPopover;
